import { clamp, nth } from 'lodash';
import { characterPoints, CUSTOM_CSS, SCORE, SHAPE_LINE_THICKNESS, STORY_SETTING_PROP } from 'common/constants';
import { FIELD_BB_ERROR_STYLE, FIELD_BB_STYLE } from 'client/components/common/BuildingBlocks/Fields/constants';
import { uniformProps } from 'admin/components/pages/Story/CardEditor/Inspector/config/other-props';
import { cssProps } from 'admin/components/pages/Story/CardEditor/Inspector/config/css-props';
import { parseScore } from 'admin/components/pages/Story/CardEditor/Inspector/CardSettings/CardSettingsModal/utils';
import { PROP_FIELD_TYPES } from '../PropField/constants';

/*
 This file contains the basic configuration for inputs by type (PropField),
 which will subsequently be displayed in <InspectorToolbar>, as well as the configuration of input
 according to type, for specific editable properties of the building block.
 */

export const PROP_FIELD_ICONS = {
	FONT_SIZE: 'fontSize',
	LINE_HEIGHT: 'lineHeight',
	TEXT_ALIGN_LEFT: 'textAlignLeft',
	TEXT_ALIGN_RIGHT: 'textAlignRight',
	TEXT_ALIGN_CENTER: 'textAlignCenter',
	DIRECTION_LTR: 'ltr',
	DIRECTION_RTL: 'rtl',
	BOX_FRAME: 'pf-box-frame',
	BOX_STACK: 'pf-box-stack',
	BOX_GRID: 'pf-box-grid',
	CELL_HEIGHT: 'pf-cell-height',
	CELL_WIDTH: 'pf-cell-width',
	GAP_ROW: 'pf-gap-row',
	GAP_COL: 'pf-gap-col',
	SHAPE_RECT: 'pf-shape-rect',
	SHAPE_CIRCLE: 'pf-shape-circle',
	SHAPE_LINE: 'pf-shape-line',
	STRETCH_X: 'pf-stretch-x',
	STRETCH_Y: 'pf-stretch-y',
	SQUARED_BTN: 'pf-squared-btn',
	ROUNDED_BTN: 'pf-rounded-btn',
	INPUT_TEXT: 'pf-input-text',
	INPUT_EMAIL: 'pf-input-email',
	INPUT_TEL: 'pf-input-tel',
	SHARE_EMAIL: 'pf-share-email',
	SHARE_FB: 'pf-share-fb',
	SHARE_WA: 'pf-share-wa',
	SHARE_TW: 'pf-share-tw',
	TIMER_NUM: 'pf-timer-num',
	PROGRESS_BAR: 'pf-progress-bar',
	PROGRESS_NUM: 'pf-progress-num',
	PROGRESS_CIRC: 'pf-progress-circ',
	GENERAL: 'pf-general',
	BORDERS: 'pf-borders',
	CSS: 'pf-css',
	EMBED: 'pf-embed',
	EFFECTS: 'pf-effects',
	LAYOUT: 'pf-layout',
	POSITION: 'pf-position',
	SETTINGS: 'pf-settings',
	SIZE: 'pf-size',
	TEXT: 'pf-text',
	LINK: 'pf-link',
	STATE: 'pf-state',
	ERROR: 'pf-error',
	BACKGROUND: 'pf-background',
	SYMBOL: 'pf-symbol',
	ANSWERS: 'pf-answers',
} as const;

export const checkbox = {
	component: PROP_FIELD_TYPES.checkbox,
};

export const toggle = {
	component: PROP_FIELD_TYPES.toggle,
};

export const color = {
	component: PROP_FIELD_TYPES.color,
};

export const text = {
	component: PROP_FIELD_TYPES.text,
};

export const textareaAutosize = {
	component: PROP_FIELD_TYPES.textareaAutosize,
	minRows: 1,
};

export const slider = {
	component: PROP_FIELD_TYPES.slider,
};

export const number: {
	component: typeof PROP_FIELD_TYPES.number;
	min: number | undefined;
	max: number | undefined;
	step: number;
	dragStep: number | undefined;
	shiftStep: number;
	decimals: number;
	unit: boolean;
	parse?: (value: string, name: string) => string;
} = {
	component: PROP_FIELD_TYPES.number,
	min: undefined,
	max: undefined,
	step: 1,
	dragStep: undefined,
	shiftStep: 10,
	decimals: 0,
	unit: true, // vw
};

export const select = {
	component: PROP_FIELD_TYPES.select,
};

export const iconSelect = {
	component: PROP_FIELD_TYPES.iconSelect,
};

export const media = {
	component: PROP_FIELD_TYPES.media,
	customData: {
		isRemovable: true,
	},
};

export const radio = {
	component: PROP_FIELD_TYPES.radio,
};

const fontFamily = {
	...select,
	options: [
		{ label: 'Varela Round', value: '"Varela Round", sans-serif', italic: true },
		{ label: 'Miriam Libre', value: '"Miriam Libre", sans-serif', italic: true },
		{ label: 'Suez One', value: '"Suez One", serif', italic: true },
		{ label: 'Secular One', value: '"Secular One", sans-serif', italic: true },
		{ label: 'Alef', value: 'Alef, sans-serif', italic: true },
		{ label: 'Assistant', value: 'Assistant, sans-serif', italic: true },
		{ label: 'Rubik', value: 'Rubik, sans-serif', italic: true },
	],
};
const fontWeight = {
	...select,
	options: [
		{ label: 'normal', value: '400' },
		{ label: 'light', value: '300' },
		{ label: 'bold', value: '700' },
		{ label: 'black', value: '900' },
	],
};
const fontSize = { ...number, decimals: 4, min: 0, icon: PROP_FIELD_ICONS.FONT_SIZE };
const lineHeight = { ...number, decimals: 4, min: 0, dragStep: 2, icon: PROP_FIELD_ICONS.LINE_HEIGHT };
const textAlign = {
	...iconSelect,
	options: [
		{ value: 'left', icon: PROP_FIELD_ICONS.TEXT_ALIGN_LEFT },
		{ value: 'center', icon: PROP_FIELD_ICONS.TEXT_ALIGN_CENTER },
		{ value: 'right', icon: PROP_FIELD_ICONS.TEXT_ALIGN_RIGHT },
	],
};
const direction = {
	...iconSelect,
	options: [
		{ value: 'ltr', icon: PROP_FIELD_ICONS.DIRECTION_LTR },
		{ value: 'rtl', icon: PROP_FIELD_ICONS.DIRECTION_RTL },
	],
};
const padding = { ...number, min: 0 };

export const propFieldsConfig: {
	[key: string]: {
		component: typeof PROP_FIELD_TYPES[keyof typeof PROP_FIELD_TYPES];
		parse?: (value: string, name: string) => string;
		format?: (value: string, name: string) => string | number;
		options?:
			| { label: string; value: string }[]
			| { value: string; icon: string }[]
			| { label: string; value: string; icon: string }[];
		icon?: typeof PROP_FIELD_ICONS[keyof typeof PROP_FIELD_ICONS];
	} & Omit<Partial<typeof textareaAutosize>, 'component'> &
		Omit<Partial<typeof color>, 'component'> &
		Omit<Partial<typeof text>, 'component'> &
		Omit<Partial<typeof number>, 'component'> &
		Omit<Partial<typeof radio>, 'component'> &
		Omit<Partial<typeof media>, 'component'> &
		Omit<Partial<typeof iconSelect>, 'component'> &
		Omit<Partial<typeof toggle>, 'component'> &
		Omit<Partial<typeof checkbox>, 'component'> &
		Omit<Partial<typeof select>, 'component'>;
} = {
	// Video
	videoSrc: media,
	videoPoster: media,
	muted: toggle,
	loop: toggle,
	controls: toggle,
	autoplay: toggle,

	// Background
	background: textareaAutosize,
	backgroundColor: color,
	backgroundSize: {
		...iconSelect,
		options: [
			{ label: 'Fill', value: 'cover' },
			{ label: 'Fit', value: 'contain' },
		],
	},
	objectFit: {
		...iconSelect,
		options: [
			{ label: 'Fill', value: 'cover' },
			{ label: 'Fit', value: 'contain' },
		],
	},
	backgroundRepeat: {
		...select,
		options: [
			{ label: 'repeat', value: 'repeat' },
			{ label: 'no-repeat', value: 'no-repeat' },
			{ label: 'repeat-x', value: 'repeat-x' },
			{ label: 'repeat-y', value: 'repeat-y' },
		],
	},
	backgroundPosition: {
		...select,
		options: [
			{ label: 'top', value: '50% 0%' },
			{ label: 'top-left', value: '0% 0%' },
			{ label: 'top-right', value: '100% 0%' },
			{ label: 'center', value: '50% 50%' },
			{ label: 'center-left', value: '0% 50%' },
			{ label: 'center-right', value: '100% 50%' },
			{ label: 'bottom', value: '50% 100%' },
			{ label: 'bottom-left', value: '0% 100%' },
			{ label: 'bottom-right', value: '100% 100%' },
		],
	},
	backgroundImage: media,
	// Padding
	padding: text,
	paddingTop: padding,
	paddingBottom: padding,
	paddingLeft: padding,
	paddingRight: padding,
	// Margin
	margin: text,
	marginTop: number,
	marginBottom: number,
	marginLeft: number,
	marginRight: number,
	// Grid
	gridTemplateRows: { ...number, min: 0 },
	gridTemplateColumns: {
		...number,
		min: 1,
		max: 24,
		unit: false, // no vw
		// view
		format: (value = '', name) => {
			// @ts-expect-error ts-migrate FIXME
			return parseInt(nth(value.toString().match(/^[^\d]*(\d+)/), 1), 10);
		},
		// store
		parse: (value = '', name) => {
			return `repeat(${clamp(+value, 1, 24)}, 1fr)`;
		},
	},
	gridAutoRows: { ...number, min: 0, icon: PROP_FIELD_ICONS.CELL_HEIGHT },
	gridRowGap: { ...number, min: 0, icon: PROP_FIELD_ICONS.GAP_ROW },
	gridColumnGap: { ...number, min: 0, icon: PROP_FIELD_ICONS.GAP_COL },
	gridAutoColumns: text,
	gridRow: text,
	gridColumn: text,
	// Box
	display: {
		...select,
		options: [
			{ label: 'block', value: 'block' },
			{ label: 'inline-block', value: 'inline-block' },
			{ label: 'flex', value: 'flex' },
			{ label: 'grid', value: 'grid' },
			{ label: 'none', value: 'none' },
		],
	},
	width: { ...number, min: 0, icon: PROP_FIELD_ICONS.CELL_WIDTH },
	minWidth: { ...number, min: 0 },
	maxWidth: { ...number, min: 0 },
	height: { ...number, min: 0, icon: PROP_FIELD_ICONS.CELL_HEIGHT },
	minHeight: { ...number, min: 0 },
	maxHeight: { ...number, min: 0 },
	border: textareaAutosize,
	borderColor: color,
	borderStyle: {
		...select,
		options: [
			{ label: 'all', value: 'solid' },
			{ label: 'top', value: 'solid none none none' },
			{ label: 'right', value: 'none solid none none' },
			{ label: 'bottom', value: 'none none solid none' },
			{ label: 'left', value: 'none none none solid' },
			{ label: 'left/right', value: 'none solid none' },
			{ label: 'top/bottom', value: 'solid none' },
		],
	},
	borderWidth: { ...number, min: 0 },
	borderRadius: { ...number, min: 0 },
	borderTopRightRadius: { ...number, min: 0 },
	borderTopLeftRadius: { ...number, min: 0 },
	borderBottomLeftRadius: { ...number, min: 0 },
	borderBottomRightRadius: { ...number, min: 0 },
	boxShadow: text,
	overflow: {
		...select,
		options: [
			{ label: 'auto', value: 'auto' },
			{ label: 'hidden', value: 'hidden' },
			{ label: 'scroll', value: 'scroll' },
			{ label: 'visible', value: 'visible' },
		],
	},
	// Text
	color,
	fontFamily,
	fontWeight,
	fontSize,
	lineHeight,
	textAlign,
	direction,
	textShadow: textareaAutosize,
	textTransform: {
		...select,
		options: [
			{ label: 'capitalize', value: 'capitalize' },
			{ label: 'uppercase', value: 'uppercase' },
			{ label: 'lowercase', value: 'lowercase' },
			{ label: 'none', value: 'none' },
		],
	},
	letterSpacing: { ...number, decimals: 4 },
	// Position
	position: {
		...select,
		options: [
			{ label: 'static', value: 'static' },
			{ label: 'relative', value: 'relative' },
			{ label: 'absolute', value: 'absolute' },
			{ label: 'fixed', value: 'fixed' },
		],
	},
	top: number,
	bottom: number,
	left: number,
	right: number,
	zIndex: number,
	float: {
		...select,
		options: [
			{ label: 'left', value: 'left' },
			{ label: 'right', value: 'right' },
			{ label: 'none', value: 'none' },
		],
	},
	clear: {
		...select,
		options: [
			{ label: 'left', value: 'left' },
			{ label: 'right', value: 'right' },
			{ label: 'both', value: 'both' },
			{ label: 'none', value: 'none' },
		],
	},
	// Flex
	flexDirection: {
		...select,
		options: [
			{ value: 'row', label: 'row' },
			{ value: 'row-reverse', label: 'row-reverse' },
			{ value: 'column', label: 'column' },
			{ value: 'column-reverse', label: 'column-reverse' },
		],
	},
	flexWrap: {
		...select,
		options: [
			{ value: 'nowrap', label: 'nowrap' },
			{ value: 'wrap', label: 'wrap' },
			{ value: 'wrap-reverse', label: 'wrap-reverse' },
		],
	},
	alignItems: {
		...select,
		options: [
			{ value: 'flex-start', label: 'flex-start' },
			{ value: 'flex-end', label: 'flex-end' },
			{ value: 'center', label: 'center' },
			{ value: 'baseline', label: 'baseline' },
			{ value: 'stretch', label: 'stretch' },
		],
	},
	justifyContent: {
		...select,
		options: [
			{ value: 'flex-start', label: 'flex-start' },
			{ value: 'flex-end', label: 'flex-end' },
			{ value: 'center', label: 'center' },
			{ value: 'baseline', label: 'baseline' },
			{ value: 'stretch', label: 'stretch' },
		],
	},
	flexGrow: text,
	flexShrink: text,
	flexBasis: text,
	opacity: { ...number, min: 0, max: 1, step: 0.1, shiftStep: 0.2, decimals: 2, unit: false },
	transform: textareaAutosize,
	transition: text,
	filter: text,
	cursor: {
		...select,
		options: [
			{ value: 'alias', label: 'alias' },
			{ value: 'all-scroll', label: 'all-scroll' },
			{ value: 'auto', label: 'auto' },
			{ value: 'cell', label: 'cell' },
			{ value: 'context-menu', label: 'context-menu' },
			{ value: 'col-resize', label: 'col-resize' },
			{ value: 'copy', label: 'copy' },
			{ value: 'crosshair', label: 'crosshair' },
			{ value: 'default', label: 'default' },
			{ value: 'e-resize', label: 'e-resize' },
			{ value: 'ew-resize', label: 'ew-resize' },
			{ value: 'grab', label: 'grab' },
			{ value: 'grabbing', label: 'grabbing' },
			{ value: 'help', label: 'help' },
			{ value: 'move', label: 'move' },
			{ value: 'n-resize', label: 'n-resize' },
			{ value: 'ne-resize', label: 'ne-resize' },
			{ value: 'nesw-resize', label: 'nesw-resize' },
			{ value: 'ns-resize', label: 'ns-resize' },
			{ value: 'nw-resize', label: 'nw-resize' },
			{ value: 'nwse-resize', label: 'nwse-resize' },
			{ value: 'no-drop', label: 'no-drop' },
			{ value: 'none', label: 'none' },
			{ value: 'not-allowed', label: 'not-allowed' },
			{ value: 'pointer', label: 'pointer' },
			{ value: 'progress', label: 'progress' },
			{ value: 'row-resize', label: 'row-resize' },
			{ value: 's-resize', label: 's-resize' },
			{ value: 'se-resize', label: 'se-resize' },
			{ value: 'sw-resize', label: 'sw-resize' },
			{ value: 'text', label: 'text' },
			{ value: 'w-resize', label: 'w-resize' },
			{ value: 'wait', label: 'wait' },
			{ value: 'zoom-in', label: 'zoom-in' },
			{ value: 'zoom-out', label: 'zoom-out' },
		],
	},
	pointerEvents: {
		...select,
		options: [
			{ value: 'none', label: 'off' },
			{ value: 'auto', label: 'on' },
		],
	},
	// svg
	[cssProps.svg.strokeWidth]: { ...number, min: 0 },
	// Custom
	[CUSTOM_CSS]: textareaAutosize,
	tag: {
		...select,
		options: [
			{ value: 'h1', label: 'h1' },
			{ value: 'h2', label: 'h2' },
			{ value: 'h3', label: 'h3' },
			{ value: 'h4', label: 'h4' },
			{ value: 'h5', label: 'h5' },
			{ value: 'h6', label: 'h6' },
			{ value: 'p', label: 'p' },
		],
	},
	src: text,
	tint: color,
	media,
	contentHeight: { ...number, min: 0, icon: PROP_FIELD_ICONS.CELL_HEIGHT },
	contentAlignY: radio,
	contentMarginTop: number,
	contentMarginBottom: number,
	contentMarginLeft: number,
	contentMarginRight: number,
	embedCode: {
		...textareaAutosize,
		// view
		format: (value = '', name) => {
			return decodeURI(value);
		},
		// store
		parse: (value = '', name) => {
			return encodeURI(value);
		},
	},
	[SHAPE_LINE_THICKNESS]: {
		...number,
		min: 0,
	},
	[uniformProps.fieldType]: { ...iconSelect },
	[uniformProps.fieldFullName]: { ...toggle },
	[uniformProps.fieldRequired]: { ...toggle },
	[uniformProps.fieldPlaceholderShow]: { ...toggle },
	[uniformProps.fieldErrorShow]: { ...select },
	[uniformProps.btnNavLink]: { ...select, options: [] },
	[uniformProps.title]: text,
	[uniformProps.alt]: text,
	[STORY_SETTING_PROP.score]: {
		...number,
		min: SCORE.MIN,
		max: SCORE.MAX,
		unit: false,
		// store
		parse: (value = '') => `${parseScore(value)}`,
	},
	[STORY_SETTING_PROP.answersMinMax]: {
		...slider,
		unit: false,
	},
	[characterPoints]: {
		...number,
		min: 0,
		decimals: 1,
		unit: false,
	},
	[STORY_SETTING_PROP.showCorrect]: toggle,
	[STORY_SETTING_PROP.showResults]: toggle,
	[cssProps.custom.trackColor]: color,
	[cssProps.custom.progressColor]: color,
	[cssProps.custom.progressDir]: { ...iconSelect },
	// timer custom props
	[uniformProps.timerType]: iconSelect,
	[uniformProps.timerTime]: { ...number, unit: false, min: 0, max: 180 },
	// answer progress custom props
	[uniformProps.answerProgressType]: iconSelect,
	// Field error
	[FIELD_BB_ERROR_STYLE.TEXT.COLOR]: color,
	[FIELD_BB_ERROR_STYLE.TEXT.FONT_FAMILY]: fontFamily,
	[FIELD_BB_ERROR_STYLE.TEXT.FONT_WEIGHT]: fontWeight,
	[FIELD_BB_ERROR_STYLE.TEXT.FONT_SIZE]: fontSize,
	[FIELD_BB_ERROR_STYLE.TEXT.LINE_HEIGHT]: lineHeight,
	[FIELD_BB_ERROR_STYLE.TEXT.TEXT_ALIGN]: textAlign,
	[FIELD_BB_ERROR_STYLE.TEXT.DIRECTION]: direction,
	[FIELD_BB_ERROR_STYLE.PADDING.LEFT]: padding,
	[FIELD_BB_ERROR_STYLE.PADDING.RIGHT]: padding,
	[FIELD_BB_ERROR_STYLE.PADDING.TOP]: padding,
	[FIELD_BB_ERROR_STYLE.PADDING.BOTTOM]: padding,
	[FIELD_BB_STYLE.PADDING.LEFT]: padding,
	[FIELD_BB_STYLE.PADDING.RIGHT]: padding,
	[FIELD_BB_STYLE.PADDING.TOP]: padding,
	[FIELD_BB_STYLE.PADDING.BOTTOM]: padding,
	[FIELD_BB_STYLE.TEXT.PH_COLOR]: color,
	[FIELD_BB_STYLE.BOX.FILL]: color,
	[FIELD_BB_STYLE.BOX.STROKE]: color,
	[FIELD_BB_STYLE.BOX.ICON_COLOR]: color,
};
